/**
 * eClinicalWorks (ECW) integration metadata.
 *
 * Runtime DOM extraction lives in `clients/extension/src/integrations/ecw/`.
 * This file is the wire-safe, no-DOM-required source of truth for:
 *   - the provider id used everywhere
 *   - host allowlist
 *   - URL-derivable page-type detection
 *
 * Anything that needs DOM access (extraction, anchor finding, button
 * injection) must NOT live here — that's the extension's responsibility.
 *
 * Mirrors `modmed.ts`. See `specs/ecw-adapter/STAGE-0-RECON.md` for the
 * captured DOM/URL evidence behind these functions.
 */

import type { EmrPageType, IntegrationMetadata } from './types';

export const ECW_INTEGRATION_ID = 'ecw' as const;

/**
 * Host suffixes ECW is known to serve webEMR from.
 *
 * `.ecwcloud.com` is ECW's cloud host; each practice gets a random per-org
 * subdomain (e.g. `bdvvdrnuidpo9mgy7eapp.ecwcloud.com`), so we match the
 * SUFFIX, never the full host. Like ModMed this is only a detection
 * FAST-PATH — on-prem / vanity deployments are still recognized structurally
 * via URL shape + DOM signature, so they don't need to be enumerated here.
 */
export const ECW_HOST_SUFFIXES = {
  production: ['.ecwcloud.com'],
  sandbox: [] as string[],
} as const;

export const ecwIntegrationMetadata: IntegrationMetadata = {
  id: ECW_INTEGRATION_ID,
  name: 'eClinicalWorks',
  description: 'Real-time patient context from eClinicalWorks webEMR',
  enabled: true,
};

/**
 * Host-independent structural test for "this is an ECW webEMR page".
 *
 * webEMR is a JSP-hosted AngularJS app served under a `/mobiledoc/jsp/webemr/`
 * path segment (with hash routing layered on top):
 *   `https://<host>/mobiledoc/jsp/webemr/index.jsp#/mobiledoc/jsp/webemr/sched...`
 *
 * We recognize it by URL SHAPE rather than hostname so on-prem / vanity
 * domains match without being enumerated. Deliberately defensive: a false
 * positive is harmless — extraction still requires ECW's Patient Hub DOM,
 * which no other app has, so nothing is captured or injected off-target.
 */
export function isEcwUrlShape(rawUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  // webEMR app path — present on cloud and on-prem deployments alike. The
  // `mobiledoc/jsp` prefix is optional to tolerate deployment variations, but
  // the `webemr` segment is required so we never match unrelated `/emr/` apps.
  return /(^|\/)(mobiledoc\/jsp\/)?webemr(\/|$)/.test(url.pathname);
}

/**
 * Pure URL → page-type classifier for ECW webEMR.
 *
 * Unlike ModMed (patient id in the hash route), ECW's patient context lives in
 * the **Patient Hub modal**, which opens over whatever view is loaded — the
 * patient id is NOT reliably in the URL. So a supported webEMR URL classifies
 * as `pageType: 'unknown'` with `emrPatientId: null`; the extension's DOM
 * extractor (`PatientHubInfo.*` bindings) is the authoritative source of
 * patient identity. Returning `isSupported: true` still lets the bridge attach
 * and observe the Hub when the user opens it.
 */
export function classifyEcwUrl(rawUrl: string): {
  isSupported: boolean;
  pageType: EmrPageType;
  emrPatientId: string | null;
} {
  if (!isEcwUrlShape(rawUrl)) {
    return { isSupported: false, pageType: 'unknown', emrPatientId: null };
  }

  // webEMR route recognized, but patient identity is modal/DOM-driven, not in
  // the URL — the bridge resolves it from the Patient Hub, not from here.
  return { isSupported: true, pageType: 'unknown', emrPatientId: null };
}
